import type { RefineThemedLayoutHeaderProps } from "@refinedev/antd";
import { useActiveAuthProvider, useGetIdentity } from "@refinedev/core";
import { Layout as AntdLayout, Popover, Typography } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetListNotification } from "../../../api/notification";
import { useGetAvatar, useGetProfile } from "../../../api/profile";
import { ReactComponent as BellIcon } from "../../../common/images/svg/Bell.svg";
import { ReactComponent as BellGradientIcon } from "../../../common/images/svg/BellGradient.svg";
import { ReactComponent as SettingIcon } from "../../../common/images/svg/Gear.svg";
import { ReactComponent as SettingGradientIcon } from "../../../common/images/svg/GearGradient.svg";
import { ReactComponent as SmartLogoHover } from "../../../common/images/svg/SmartIconHover.svg";
import avatarDefault from "../../../common/images/svg/UserCircleDefault.svg";
import avatarDefaultHover from "../../../common/images/svg/UserCircleDefaultHover.svg";
import ConfirmModal from "../../../components/modal/confirmModal";
import { ONBOARDING_STATUS_KEY, TOKEN_KEY } from "../../../components/providers/authProvider";
import { useNotification } from "../../../components/providers/notification/useNotification";
import { setPermission, usePermission } from "../../../store/use-permission";
import { useAvatarStore } from "../../../store/use-refetch-avatar";
import { useTitleHeaderStore } from "../../../store/use-title-header-store";
import { socketConnect, socketDisconnect } from "../../../utils/socket";
import "./header.css";
import NotiSetting from "./notiSetting";
import UserDetails from "./userDetails";
import UserSetting from "./userSetting";
import { useReloadSocket } from "../../../utils/hooks/useReloadSocket";

const TYPE_ORDER = {
  ONCHAIN_WITHDRAWAL: "crypto-withdrawals",
  OFFCHAIN_WITHDRAWAL: "crypto-withdrawals",
  ONCHAIN_DEPOSIT: "crypto-deposits",
  OFFCHAIN_DEPOSIT: "crypto-deposits",
  EXCHANGE_MONEY: "exchange",
};

const TYPE_CURRENCY = {
  FIAT: "fiat",
  CRYPTO: "crypto",
};

const TYPE_HISTORY = {
  ORDER: "order",
  PAYMENT_REQUEST: "payment_request",
  PAYMENT_ORDER: "payment_order",
  TRANSACTION: "transaction",
};

export const ThemedHeader: React.FC<RefineThemedLayoutHeaderProps> = () => {
  const authProvider = useActiveAuthProvider();
  const location = useLocation();
  const { open: openNotification } = useNotification();
  const statusUser = localStorage.getItem("status_user");

  const { data: user } = useGetIdentity({
    v3LegacyAuthProviderCompatible: Boolean(authProvider?.isLegacy),
  });

  const socket = useReloadSocket();
  const navigate = useNavigate();
  const { titleHeader } = useTitleHeaderStore() as any;
  const { permission } = usePermission() as any;
  const { updatedAt } = useAvatarStore() as any;
  const [openUserDetails, setOpenUserDetails] = useState(false);
  const [openSetting, setOpenSetting] = useState(false);
  const [notiSetting, setNotiSetting] = useState(false);
  const [avatarUUID, setAvatarUUID] = useState<string>("");
  const [avatarURL, setAvatarURL] = useState<string>("");
  const [hoverNoti, setHoverNoti] = useState(false);
  const [hoverSetting, setHoverSetting] = useState(false);
  const [hoverUser, setHoverUser] = useState(false);

  const authorized = user && user.email;

  const { data, refetch: refeatchNoti } = useGetListNotification({
    start: 0,
    end: 10,
    type: "UN_READ",
  });

  const {
    data: infoProfile = { data: { data: {} } },
    refetch,
    isFetching,
  } = useGetProfile();

  const {
    data: avatar = { data: { data: { url: "" } } },
    isFetching: fetchingAvatar,
  } = useGetAvatar(avatarUUID);

  const onOpenChange = useCallback((open: boolean) => {
    setOpenUserDetails(open);
  }, []);

  const onOpenSetting = useCallback((open: boolean) => {
    setOpenSetting(open);
  }, []);

  // useEffect(() => {
  //   socket.connect();
  //   return () => {
  //     socket.disconnect();
  //   };
  // }, [socket, titleHeader]);

  useEffect(() => {
    if (!openNotification) return;
    const handleNewUpdated = (data: {
      title?: string;
      type?: string;
      metadata: {
        title: string;
        message: string;
        orderId: number;
        type: string;
        transactionId?: number;
        currencyId?: number;
        currencyType?: string;
      };
    }) => {
      openNotification({
        key: "new_info",
        message: data?.metadata?.message || "New notification",
        type: "info",
        undoableTimeout: 10,
        onClick: () => {
          const typeCurrencyKey = data?.metadata?.currencyType as keyof typeof TYPE_CURRENCY
          switch (data?.type) {
            case TYPE_HISTORY.ORDER: {
              const typeKey = data?.metadata?.type as keyof typeof TYPE_ORDER;
              navigate(
                `/history?tab=${TYPE_CURRENCY[typeCurrencyKey]}&orderId=${data?.metadata?.orderId}&type=${TYPE_ORDER[typeKey]}`
              );
              break;
            }
            case TYPE_HISTORY.PAYMENT_REQUEST:
              navigate(
                `/history?tab=${TYPE_CURRENCY[typeCurrencyKey]}&orderId=${data?.metadata?.orderId}&type=send-money`
              );
              break;
            case TYPE_HISTORY.PAYMENT_ORDER:
              navigate(
                `/history?tab=${TYPE_CURRENCY[typeCurrencyKey]}&orderId=${data?.metadata?.orderId}&type=request-money`
              );
              break;
            case TYPE_HISTORY.TRANSACTION:
              navigate(
                `/home?type=${data?.metadata?.currencyType}&currency=${data?.metadata?.currencyId}&transactionId=${data?.metadata?.transactionId}&isNotification=true`
              );
              break;
          }
        },
      });
    };
    socket.on(`user.alerts`, handleNewUpdated);

    return () => {
      socket.off(`user.alerts`, handleNewUpdated);
    };
  }, [socket, openNotification]);

  useEffect(() => {
    const handleNotificationUpdated = () => {
      refeatchNoti();
    };
    socket.on(`user.notifications.created`, handleNotificationUpdated);

    return () => {
      socket.off(`user.notifications.created`, handleNotificationUpdated);
    };
  }, [socket, refeatchNoti]);

  useEffect(() => {
    if (!infoProfile) return;
    if (!infoProfile?.data?.data?.avatar_uuid) setAvatarUUID("");
    else setAvatarUUID(infoProfile?.data?.data?.avatar_uuid);
    const wcStatus = infoProfile?.data?.data?.worldCheckVerification?.status;
    const onboardingStatus = infoProfile?.data?.data?.onboardingStatus;
    if (wcStatus) {
      if (
        !statusUser ||
        (statusUser !== wcStatus && !isFetching)
      ) {
        localStorage.setItem("status_user", wcStatus);
      }
    }
    if (onboardingStatus) {
      localStorage.setItem(ONBOARDING_STATUS_KEY, onboardingStatus);
    }
  }, [infoProfile, statusUser, isFetching]);

  useEffect(() => {
    const environment = process.env.REACT_APP_ENVIRONMENT;
    setAvatarURL(
      environment === "development"
        ? avatar.data.data.url.replace(
          "http://172.188.80.191:10000/",
          "https://azurite.dev.smart.pixitech.asia/"
        )
        : avatar.data.data.url
    );
  }, [avatar.data.data.url, fetchingAvatar]);

  useEffect(() => {
    refetch();
  }, [location, updatedAt]);

  if (!authorized) {
    return null;
  }

  return (
    <AntdLayout.Header className={"header"}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginRight: 40,
        }}
      >
        <SmartLogoHover
          style={{
            width: 39,
            height: 39,
            minHeight: 39,
            minWidth: 39,
            marginBottom: "5px",
            cursor: "pointer",
          }}
          onClick={() => navigate("/home")}
          className="icon-animation"
        />
        <Typography className="layout-header-title">{titleHeader}</Typography>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: 24,
          alignItems: "center",
          height: "fit-content",
        }}
      >
        <Popover
          content={
            <NotiSetting
              setNotiSetting={setNotiSetting}
              socket={socket}
              refeatchNoti={refeatchNoti}
            />
          }
          trigger="click"
          arrow={false}
          overlayInnerStyle={{ borderRadius: "12px", padding: 0 }}
          placement={"bottomRight"}
          open={notiSetting}
          onOpenChange={setNotiSetting}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              position: "relative",
            }}
            onMouseOver={() => setHoverNoti(true)}
            onMouseOut={() => setHoverNoti(false)}
          >
            {hoverNoti || notiSetting ? (
              <BellGradientIcon className="icon-animation" />
            ) : (
              <BellIcon className="icon-animation" />
            )}
            {data?.data?.data?.total ? (
              <div className="unread-noti"></div>
            ) : null}
          </div>
        </Popover>
        <Popover
          content={<UserSetting setOpenSetting={setOpenSetting} />}
          trigger="click"
          arrow={false}
          overlayInnerStyle={{ borderRadius: "12px", padding: 0 }}
          placement={"bottomRight"}
          open={openSetting}
          onOpenChange={onOpenSetting}
        >
          <div
            style={{ display: "flex", alignItems: "center" }}
            onMouseOver={() => setHoverSetting(true)}
            onMouseOut={() => setHoverSetting(false)}
          >
            {hoverSetting || openSetting ? (
              <SettingGradientIcon className="icon-animation" />
            ) : (
              <SettingIcon className="icon-animation" />
            )}
          </div>
        </Popover>
        <Popover
          content={
            <UserDetails
              setOpenUserDetails={setOpenUserDetails}
              infoProfile={infoProfile}
              avatar={avatarUUID ? avatarURL : avatarDefault}
            />
          }
          trigger="click"
          arrow={false}
          overlayInnerStyle={{ borderRadius: "2px", padding: 0 }}
          placement={"bottomRight"}
          open={openUserDetails}
          onOpenChange={onOpenChange}
        >
          <div
            className={`icon-animation header-user ${avatarURL && (hoverUser || openUserDetails)
              ? "header-user-focus"
              : ""
              }`}
            onMouseOver={() => setHoverUser(true)}
            onMouseOut={() => setHoverUser(false)}
          >
            {hoverUser || openUserDetails ? (
              <img
                className="avt-user"
                alt="avt-user"
                src={avatarUUID ? avatarURL : avatarDefaultHover}
              />
            ) : (
              <img
                className="avt-user"
                alt="avt-user"
                src={avatarUUID ? avatarURL : avatarDefault}
              />
            )}
          </div>
        </Popover>
      </div>
      <ConfirmModal
        type="error"
        title="Access Unavailable"
        description="Your access to the system is currently unavailable."
        open={!permission}
        showButtonCancel={false}
        onCancel={() => null}
        onSubmit={() => {
          socketDisconnect();
          localStorage.clear();
          window.location.href = "/app/login";
          setPermission(true);
        }}
        textSubmit="OK"
        className="confirm-modal-z-index"
      />
    </AntdLayout.Header>
  );
};
